import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const AccessControlPanel = ({ onGrantAccess, onDenyAccess, tenantId }) => {
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (tenantId) {
      loadMembers();
    }
  }, [tenantId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('tenant_members')?.select(`
          *,
          user_profiles(id, full_name, email, avatar_url),
          member_subscriptions(status, end_date)
        `)?.eq('tenant_id', tenantId)?.eq('role', 'member')?.order('joined_at', { ascending: false });

      if (error) throw error;
      setMembers(data || []);
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members?.filter(member => {
    const matchesSearch = 
      member?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && member?.is_active) ||
      (filterStatus === 'inactive' && !member?.is_active);

    return matchesSearch && matchesStatus;
  });

  const handleToggleAccess = async (member) => {
    if (member?.is_active) {
      await onDenyAccess(member?.user_id);
    } else {
      await onGrantAccess(member?.user_id);
    }
    loadMembers();
  };

  return (
    <div className="bg-card rounded-xl shadow-md border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2 mb-4">
          <Icon name="Shield" size={24} color="var(--color-primary)" />
          Access Control Management
        </h2>

        {/* Search and Filters */}
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Icon name="Search" size={18} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              All
            </Button>
            <Button
              variant={filterStatus === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('active')}
            >
              Active
            </Button>
            <Button
              variant={filterStatus === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('inactive')}
            >
              Inactive
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-2 text-primary" />
            <p className="text-muted-foreground">Loading members...</p>
          </div>
        ) : filteredMembers?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No members found</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {filteredMembers?.map((member) => (
              <div
                key={member?.id}
                className="p-4 bg-background rounded-lg border border-border flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {member?.user_profiles?.avatar_url ? (
                      <img
                        src={member?.user_profiles?.avatar_url}
                        alt={member?.user_profiles?.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={20} className="text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      {member?.user_profiles?.full_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {member?.user_profiles?.email}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          member?.is_active
                            ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                        }`}
                      >
                        {member?.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {member?.member_subscriptions?.[0] && (
                        <span className="text-xs text-muted-foreground capitalize">
                          {member?.member_subscriptions?.[0]?.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  variant={member?.is_active ? 'destructive' : 'default'}
                  size="sm"
                  onClick={() => handleToggleAccess(member)}
                  className="flex items-center gap-2"
                >
                  <Icon name={member?.is_active ? 'Ban' : 'CheckCircle'} size={16} />
                  {member?.is_active ? 'Deny Access' : 'Grant Access'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessControlPanel;